import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { EntityType } from '@/modules/common/types/entity-type';
import { ReservedUserGroupName } from '@/modules/common/types/reserved-user-group-name';
import { useConnectedEntityIntegrations } from '@/modules/onboarding/common/hooks/use-connected-entity-integrations';
import { EntityReconciliationConfig } from '@/modules/onboarding/common/types/entity-reconciliation-config';
import { invalidateOnboardingTeamMemberQueries } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';
import { useUserProfile } from '@/modules/settings/hooks/account/use-user-profile';

import { EmployeesTableFormValues, employeesTableSchema } from '../api/employee-schema';
import {
  deleteEmployees,
  getEmployees,
  getEmployeesValidationReport,
  submitEmployees,
  updateEmployeesBulk,
} from '../api/employees';
import { AddOnboardingMembersModal } from '../components/add-members-modal';
import { ImportOnboardingMembersModal } from '../components/import-members-modal/import-onboarding-members-modal';
import { ResolveOnboardingMembersConflictsModal } from '../components/resolve-duplicates-modal/resolve-onboarding-members-conflicts-modal';
import { getEmployeeColumns, TeamColumnDeps } from '../constants/columns';
import { EmployeeFieldMapper, EmployeeFieldValues } from '../mappers/employee-field-mapper';
import { Employee, TeamDataReconciliationQueryKeys } from '../types/employees.types';

const TEAM_I18N_NAMESPACE = 'authenticated.onboarding.team';

export function useTeamReconciliationConfig(): EntityReconciliationConfig<
  Employee,
  EmployeesTableFormValues,
  EmployeeFieldValues
> {
  const { t } = useTranslation([TEAM_I18N_NAMESPACE]);
  const { integrations: connectedIntegrations } = useConnectedEntityIntegrations(EntityType.User);
  const { userProfile } = useUserProfile();

  const extraColumnDeps: TeamColumnDeps = useMemo(
    () => ({
      connectedIntegrations,
      currentUserEmail: userProfile?.email,
      isCurrentUserSuperAdmin: userProfile?.userGroup.name === ReservedUserGroupName.SUPER_ADMIN,
    }),
    [connectedIntegrations, userProfile?.email, userProfile?.userGroup.name]
  );

  return {
    entityKey: 'team',
    tableId: 'onboarding-team',
    integrationColumnsCount: connectedIntegrations.length,

    i18nNamespaces: ['common', TEAM_I18N_NAMESPACE, 'authenticated.onboarding.common'],
    i18nKeys: {
      searchPlaceholder: `${TEAM_I18N_NAMESPACE}:filters.searchPlaceholder`,
      searchResultAll: `${TEAM_I18N_NAMESPACE}:searchResult.all`,
      searchResultCount: `${TEAM_I18N_NAMESPACE}:searchResult.count`,
      searchResultSelected: `${TEAM_I18N_NAMESPACE}:searchResult.selected`,
      deleteButton: `${TEAM_I18N_NAMESPACE}:delete`,
      clearSelection: `${TEAM_I18N_NAMESPACE}:clearSelection`,
      warningsModalTitle: `${TEAM_I18N_NAMESPACE}:warningsModal.title`,
      warningsModalText: `${TEAM_I18N_NAMESPACE}:warningsModal.text`,
      warningsModalConfirm: `${TEAM_I18N_NAMESPACE}:warningsModal.confirm`,
      warningsModalCancel: `${TEAM_I18N_NAMESPACE}:warningsModal.cancel`,
      noDataYet: `${TEAM_I18N_NAMESPACE}:noMembersYet`,
      noDataDescription: `${TEAM_I18N_NAMESPACE}:noMembersDescription`,
      submitEntitiesWithErrorsExist: `${TEAM_I18N_NAMESPACE}:submit.entitiesWithErrorsExist`,
      submitDuplicateEntitiesExists: `${TEAM_I18N_NAMESPACE}:submit.duplicateEntitiesExists`,
      deleteErrorFallback: 'authenticated.team:api.error.deleteFailed',
      updateErrorFallback: 'authenticated.team:api.error.updateFailed',
      deleteModalTitle: `${TEAM_I18N_NAMESPACE}:deleteModal.title`,
      deleteModalText: `${TEAM_I18N_NAMESPACE}:deleteModal.text`,
      deleteModalCancel: `${TEAM_I18N_NAMESPACE}:deleteModal.cancel`,
      deleteModalConfirm: `${TEAM_I18N_NAMESPACE}:deleteModal.confirm`,
      deleteModalAndMore: `${TEAM_I18N_NAMESPACE}:deleteModal.andMore`,
    },

    queryKeys: {
      entities: TeamDataReconciliationQueryKeys.TeamMembers,
      validationReport: TeamDataReconciliationQueryKeys.ValidationReport,
    },
    getEntities: getEmployees,
    getValidationReport: getEmployeesValidationReport,

    getSchema: schemaT => employeesTableSchema(schemaT),
    defaultFormValues: { employees: [] } as unknown as EmployeesTableFormValues,
    formFieldPrefix: 'employees',
    getColumns: ({ t: columnT, form, getFormIndex, extraDeps }) =>
      getEmployeeColumns({ t: columnT, form, getFormIndex, deps: extraDeps as TeamColumnDeps }),

    duplicateFieldExtractors: [
      (item: { name: string }) => item.name,
      (item: { email?: string }) => item.email ?? '',
    ],

    fieldMapper: new EmployeeFieldMapper(),
    buildUpdateRequestItem: (entityId, changes) => ({
      id: entityId,
      email: changes.email || undefined,
      name: changes.name,
    }),

    deleteEntities: ids => deleteEmployees({ employeeIds: ids }),
    updateEntitiesBulk: updateEmployeesBulk,
    submitEntities: submitEmployees,
    invalidateQueries: invalidateOnboardingTeamMemberQueries,

    renderAddButton: onSuccess => React.createElement(AddOnboardingMembersModal, { onSuccess }),
    renderImportButton: onSuccess =>
      React.createElement(ImportOnboardingMembersModal, { onSuccess }),
    renderResolveDuplicatesModal: (selectedItems, onSuccess, options) => {
      const superAdminEmail =
        extraColumnDeps.isCurrentUserSuperAdmin &&
        extraColumnDeps.currentUserEmail &&
        selectedItems.some(item => item.email === extraColumnDeps.currentUserEmail)
          ? extraColumnDeps.currentUserEmail
          : null;

      return React.createElement(ResolveOnboardingMembersConflictsModal, {
        selectedItems,
        onSuccess,
        connectedIntegrations,
        disabled: options?.disabled,
        disabledTooltip: options?.disabledTooltip,
        superAdminEmail,
      });
    },

    useExtraColumnDeps: () => extraColumnDeps,

    deleteProtection: selectedRows => {
      const hasChampion = selectedRows.some(row => row.isChampion);
      return {
        disabled: hasChampion,
        tooltip: hasChampion ? t(`${TEAM_I18N_NAMESPACE}:cantDeleteChampionTooltip`) : undefined,
      };
    },
  };
}
